// -----------------------------------------------------------------------------
// analytics.js — Answer-engine + GA4 event helpers
//
// Objectives:
//   1. Emit standardized event names into window.dataLayer so any GA4 / GTM
//      container can consume them without a container-side rewrite.
//   2. Preserve utm_source=chatgpt.com (and any other UTM parameters)
//      across a session so first-touch attribution survives internal navigation.
//   3. Fire a single "chatgpt_referral" event on the first pageview whose
//      referrer or UTM identifies ChatGPT.
//   4. Delegate tel:/mailto: click tracking so every phone/email link on the
//      site emits an event without requiring per-component instrumentation.
//
// Design:
//   • No hard dependency on a specific GA4 measurement ID. If gtag() is loaded
//     (typical GA4/GTM install), events flow via dataLayer.push().
//   • If no measurement tag is loaded, calls are silent no-ops. That means
//     this module is safe to ship before the client's GA4 property is wired.
//   • All UTM state uses sessionStorage — no cookies, no PII, no consent
//     dependency beyond ordinary first-party analytics.
// -----------------------------------------------------------------------------

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "msclkid",
];

const SESSION_KEY = "lsc.utm";
const CHATGPT_FIRED_KEY = "lsc.chatgpt_fired";

function safeDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function pushEvent(event, payload = {}) {
  const dl = safeDataLayer();
  if (!dl) return;
  const utm = readUtm();
  dl.push({ event, ...utm, ...payload, ts: new Date().toISOString() });
}

function readUtm() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[analytics] readUtm failed:", err);
    return {};
  }
}

function captureUtmFromUrl() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const captured = {};
    for (const key of UTM_KEYS) {
      const val = params.get(key);
      if (val) captured[key] = val;
    }
    if (Object.keys(captured).length === 0) return;
    const existing = readUtm();
    const merged = { ...existing, ...captured };
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(merged));
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[analytics] captureUtm failed:", err);
  }
}

function detectChatGptReferral() {
  if (typeof window === "undefined") return false;
  const ref = document.referrer || "";
  const utm = readUtm();
  const src = (utm.utm_source || "").toLowerCase();
  return (
    ref.includes("chatgpt.com") ||
    ref.includes("chat.openai.com") ||
    src === "chatgpt.com" ||
    src === "chatgpt" ||
    src === "openai"
  );
}

function fireChatGptOnce() {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(CHATGPT_FIRED_KEY)) return;
    if (!detectChatGptReferral()) return;
    window.sessionStorage.setItem(CHATGPT_FIRED_KEY, "1");
    pushEvent("chatgpt_referral", {
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || null,
    });
  } catch (err) {
    if (typeof console !== "undefined") console.warn("[analytics] chatgpt referral fire failed:", err);
  }
}

// -----------------------------------------------------------------------------
// Delegated click tracking — one listener for the whole document.
// -----------------------------------------------------------------------------
function installLinkTracking() {
  if (typeof window === "undefined") return;
  if (window.__lscLinkTrackingInstalled) return;
  window.__lscLinkTrackingInstalled = true;

  document.addEventListener(
    "click",
    (e) => {
      const anchor = e.target && e.target.closest && e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        pushEvent("phone_click", {
          phone: href.replace(/^tel:/, ""),
          link_text: (anchor.textContent || "").trim().slice(0, 80),
          location: window.location.pathname,
        });
      } else if (href.startsWith("mailto:")) {
        pushEvent("email_click", {
          email: href.replace(/^mailto:/, "").split("?")[0],
          link_text: (anchor.textContent || "").trim().slice(0, 80),
          location: window.location.pathname,
        });
      }
    },
    true,
  );
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------
export function initAnalytics() {
  captureUtmFromUrl();
  fireChatGptOnce();
  installLinkTracking();
}

export function trackIntakeStart(source = "unknown") {
  pushEvent("intake_start", { form_source: source, path: typeof window !== "undefined" ? window.location.pathname : "" });
}

export function trackIntakeSubmit(source = "unknown", extra = {}) {
  pushEvent("intake_submit", {
    form_source: source,
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ...extra,
  });
}

export function trackEvent(event, payload = {}) {
  pushEvent(event, payload);
}

// -----------------------------------------------------------------------------
// Authority-hub instrumentation
// -----------------------------------------------------------------------------
// Fired once on mount from each canonical authority-hub page (APA, Succession,
// Business-Owner Trust, Trademark, Physician). Payload captures the slug, the
// document title, and the count of curated priority FAQs surfaced above the
// fold so we can measure how often answer-engine landings correspond to the
// pages we have most deeply optimized.
export function trackAuthorityPageView({ page_slug, page_title, priority_faq_count = 0 } = {}) {
  pushEvent("authority_page_view", {
    page_slug,
    page_title,
    priority_faq_count,
    path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}

// Fired each time a user expands a curated priority FAQ. Position is 1-indexed
// and matches the order in which the FAQ is rendered in the page (which also
// matches its order in FAQPage JSON-LD). No PII — only the question text.
export function trackPriorityFaqOpen({ page_slug, faq_position, faq_question } = {}) {
  pushEvent("priority_faq_open", {
    page_slug,
    faq_position,
    faq_question: (faq_question || "").slice(0, 160),
    path: typeof window !== "undefined" ? window.location.pathname : "",
  });
}
